import React, { useState, useEffect } from "react";
import BuildingIcon from "../../../svgs/building.svg";
import LocationIcon from "../../../svgs/supplier-address.svg";
import CardIcon from "../../../svgs/card.svg";
import { useNotification } from "../../../../Notifications/NotificationProvider";
import { InventoryService } from "../../../network/InventoryService";
import FormField from "../../../../Shared/Form/FormField";

const WarehouseCreateModal = ({
  showWarehouseCreateModal,
  setShowWarehouseCreateModal,
  isEditable = true,
  warehouseData = null,
  onWarehouseCreated,
}) => {
  const dispatchNotification = useNotification();
  const [form, setForm] = useState({
    warehouseName: "",
    warehouseAddress: "",
    warehouseLatitude: "",
    warehouseLongitude: "",
    warehousePOC: "",
    warehouseOwner: "",
  });
  const [formError, setFormError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (warehouseData && !isEditable) {
      setForm({
        warehouseName: warehouseData.name || "",
        warehouseAddress: warehouseData.address || "",
        warehouseLatitude: warehouseData.latitude || "",
        warehouseLongitude: warehouseData.longitude || "",
        warehousePOC: warehouseData.poc || "",
        warehouseOwner: warehouseData.owner || "",
      });
    }
  }, [warehouseData, isEditable]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.warehouseName ||
      !form.warehouseAddress ||
      !form.warehouseLatitude ||
      !form.warehouseLongitude ||
      !form.warehousePOC ||
      !form.warehouseOwner
    ) {
      setFormError("Please fill all required fields.");
      return;
    }

    const lat = parseFloat(form.warehouseLatitude);
    const lng = parseFloat(form.warehouseLongitude);

    if (isNaN(lat) || lat < -90 || lat > 90) {
      setFormError("Please enter a valid latitude (-90 to 90).");
      return;
    }

    if (isNaN(lng) || lng < -180 || lng > 180) {
      setFormError("Please enter a valid longitude (-180 to 180).");
      return;
    }

    setIsCreating(true);
    setFormError("");

    try {
      const warehouseRequestData = {
        name: form.warehouseName,
        address: form.warehouseAddress,
        poc: form.warehousePOC,
        owner: form.warehouseOwner,
        lat: lat,
        lng: lng,
      };

      const response =
        await InventoryService.createWarehouse(warehouseRequestData);

      if (response.is_error === 0) {
        dispatchNotification({
          type: "SUCCESS",
          message: "Warehouse created successfully!",
        });

        setForm({
          warehouseName: "",
          warehouseAddress: "",
          warehouseLatitude: "",
          warehouseLongitude: "",
          warehousePOC: "",
          warehouseOwner: "",
        });

        setShowWarehouseCreateModal(false);

        if (onWarehouseCreated) {
          onWarehouseCreated();
        }
      } else {
        throw new Error(response.message || "Failed to create warehouse");
      }
    } catch (error) {
      console.error("Error creating warehouse:", error);

      let errorMessage = "Failed to create warehouse. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setFormError(errorMessage);

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div
      className="inventory-modal-overlay"
      onClick={() => !isCreating && setShowWarehouseCreateModal(false)}
    >
      <form
        className="inventory-modal inventory-warehouse-create-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="inventory-modal-header">
          <h3>{isEditable ? "Create New Warehouse" : "Warehouse Details"}</h3>
          <button
            className="close-btn"
            type="button"
            onClick={() => !isCreating && setShowWarehouseCreateModal(false)}
            disabled={isCreating}
          >
            ×
          </button>
        </div>

        <div className="inventory-modal-body">
          <FormField
            id="warehouse-name"
            name="warehouseName"
            title="Warehouse Name"
            type="text"
            icon={BuildingIcon}
            value={form.warehouseName}
            onChange={handleInputChange}
            placeholder="Warehouse name ..."
            disabled={!isEditable || isCreating}
            required
          />

          <FormField
            id="warehouse-address"
            name="warehouseAddress"
            title="Warehouse Address"
            type="text"
            icon={LocationIcon}
            value={form.warehouseAddress}
            onChange={handleInputChange}
            placeholder="Warehouse address ..."
            disabled={!isEditable || isCreating}
            required
          />

          <FormField
            id="warehouse-latitude"
            name="warehouseLatitude"
            title="Warehouse Latitude"
            type="number"
            icon={LocationIcon}
            value={form.warehouseLatitude}
            onChange={handleInputChange}
            placeholder="23.7808"
            disabled={!isEditable || isCreating}
            required
          />

          <FormField
            id="warehouse-longitude"
            name="warehouseLongitude"
            title="Warehouse Longitude"
            type="number"
            icon={LocationIcon}
            value={form.warehouseLongitude}
            onChange={handleInputChange}
            placeholder="90.4092"
            disabled={!isEditable || isCreating}
            required
          />

          <FormField
            id="warehouse-poc"
            name="warehousePOC"
            title="Warehouse POC"
            type="text"
            icon={CardIcon}
            value={form.warehousePOC}
            onChange={handleInputChange}
            placeholder="Warehouse POC ..."
            disabled={!isEditable || isCreating}
            required
          />

          <FormField
            id="warehouse-owner"
            name="warehouseOwner"
            title="Warehouse Owner"
            type="text"
            icon={BuildingIcon}
            value={form.warehouseOwner}
            onChange={handleInputChange}
            placeholder="Warehouse owner ..."
            disabled={!isEditable || isCreating}
            required
          />

          {formError && <div className="common-form-error">{formError}</div>}
        </div>

        {isEditable && (
          <div className="inventory-modal-footer">
            <button
              className="cancel-btn"
              type="button"
              onClick={() => setShowWarehouseCreateModal(false)}
              disabled={isCreating}
            >
              Cancel
            </button>
            <button className="submit-btn" type="submit" disabled={isCreating}>
              {isCreating ? "Creating..." : "Create"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default WarehouseCreateModal;
